import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Edit,
  SimpleForm,
  TextInput,
  FileInput,
  ImageField,
  Toolbar,
  SaveButton,
  useNotify,
  useRedirect,
  useUpdate,
  ReferenceArrayInput,
  SelectArrayInput,
  ArrayInput,
  SimpleFormIterator,
  required
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';

import { htmlToBlocks, blocksToHtml } from '../../../helpers/htmlUtils';
import { uploadImage } from '../../../config/services/strapi/strapi';

function IndustriaEdit(props: any) {
  const [imagesChanged, setImagesChanged] = useState({
    logo: false,
    icono: false,
    banner: false,
    imagen_beneficios: false,
    experiencia: false,
  });

  const { id } = useParams();

  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();

  const processImages = async(images: any, token: string) => {
    const result: Record<string, any> = {};

    if (imagesChanged.logo && images.logo?.rawFile) {
      result.logo = await uploadImage(images.logo.rawFile, token);
    }

    if (imagesChanged.icono && images.icono?.rawFile) {
      result.icono = await uploadImage(images.icono.rawFile, token);
    }

    if (imagesChanged.banner && images.banner?.rawFile) {
      result.banner = await uploadImage(images.banner.rawFile, token);
    }

    if (imagesChanged.imagen_beneficios && images.imagen_beneficios?.rawFile) {
      result.imagen_beneficios = await uploadImage(images.imagen_beneficios.rawFile, token);
    }

    result.experiencia = await Promise.all(
      images.experiencia.map(async (experiencia: any) => {
        const imagen = experiencia.imagen;
        const procesada = await uploadImage(imagen.rawFile, token);
        return { ...experiencia, imagen: procesada };
      })
    );

    return result;
  }

  const handleSubmit = async(data: any) => {
    try {
      const token = localStorage.getItem('strapi_jwt');
      if (!token) {
        notify('No se encontró el token de autenticación', { type: 'error' });
        return;
      }

      const payload: Record<string, any> = {
        nombre: data.nombre,
        descripcion: typeof data.descripcion === 'string' ? htmlToBlocks(data.descripcion) : (data.descripcion || []),
        beneficios: typeof data.beneficios === 'string' ? htmlToBlocks(data.beneficios) : (data.beneficios || []),
        experiencia: data.experiencia.map((block: any) => ({
          imagen: block.imagen,
          contenido: typeof block.contenido === 'string' ? htmlToBlocks(block.contenido) : (block.contenido || []),
          __component: 'shared.caracteristica',
        })),
        nombre_industria: data.nombre_industria,
        slug: data.slug,
        titulo_pestana: data.titulo_pestana,
        soluciones_energeticas: data.soluciones_energeticas,
        logo: data.logo,
        icono: data.icono,
        banner: data.banner,
        imagen_beneficios: data.imagen_beneficios,
      }

      const processedImages = await processImages(payload, token);
      const finalPayload = { ...payload, ...processedImages };

      update(
        'industries',
        { data: finalPayload, id: id },
        {
          onSuccess: () => {
            notify('Elemento actualizado');
            redirect('list', 'industries');
          },
          onError: (error: any) => {
            notify(`Error: ${error.message}`, { type: 'error' });
          },
        }
      );
    }
    catch (error) {
      notify('Error al actualizar la industria', { type: 'error' });
      console.error('IndustriaEdit.handleSubmit - Error:', error);
    }
  }

  useEffect(() => {
    document.title = 'Editar Industria';
  }, []);

  return (
    <Edit {...props}>
      <SimpleForm onSubmit={handleSubmit} toolbar={<Toolbar><SaveButton alwaysEnable /></Toolbar>}>
        <TextInput source="id" disabled />
        <TextInput source="nombre" validate={[required()]} />
        <FileInput source="logo" label="Logo" onChange={() => setImagesChanged({ ...imagesChanged, logo: true })}>
          <>
            {imagesChanged.logo && <ImageField source="src" title="title" />}
            {!imagesChanged.logo && <ImageField source="url" title="title" />}
          </>
        </FileInput>
        <FileInput source="icono" label="Icono" validate={[required()]} onChange={() => setImagesChanged({ ...imagesChanged, icono: true })}>
          <>
            {imagesChanged.icono && <ImageField source="src" title="title" />}
            {!imagesChanged.icono && <ImageField source="url" title="title" />}
          </>
        </FileInput>
        <RichTextInput source="descripcion" format={blocksToHtml} parse={htmlToBlocks} />
        <RichTextInput source="beneficios" format={blocksToHtml} parse={htmlToBlocks} />
        <ArrayInput source="experiencia" label="Experiencia">
          <SimpleFormIterator>
            <FileInput source="imagen" label="Imagen" className='mt-8' onChange={() => setImagesChanged({ ...imagesChanged, experiencia: true })}>
              <>
                {imagesChanged.experiencia && <ImageField source="src" />}
                {!imagesChanged.experiencia && <ImageField source="url" />}
              </>
            </FileInput>
            <RichTextInput
              source="contenido"
              label="Contenido"
              format={blocksToHtml}
              parse={htmlToBlocks}
            />
          </SimpleFormIterator>
        </ArrayInput>
        <TextInput source="nombre_industria" validate={[required()]} />
        <TextInput source="slug" validate={[required()]} />
        <TextInput source="titulo_pestana" validate={[required()]} />
        <FileInput source="banner" label="Imagen de Banner" onChange={() => setImagesChanged({ ...imagesChanged, banner: true })}>
          <>
            {imagesChanged.banner && <ImageField source="src" title="title" />}
            {!imagesChanged.banner && <ImageField source="url" title="title" />}
          </>
        </FileInput>
        <FileInput source="imagen_beneficios" label="Imagen de Beneficios" onChange={() => setImagesChanged({ ...imagesChanged, imagen_beneficios: true })}>
          <>
            {imagesChanged.imagen_beneficios && <ImageField source="src" title="title" />}
            {!imagesChanged.imagen_beneficios && <ImageField source="url" title="title" />}
          </>
        </FileInput>
        <ReferenceArrayInput source='soluciones_energeticas' reference='solucion-energeticas' label='Soluciones Energéticas'>
          <SelectArrayInput optionText="nombre" />
        </ReferenceArrayInput>
      </SimpleForm>
    </Edit>
  )
}

export default IndustriaEdit